import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Phone, ArrowRight, Warehouse, Truck, Package, ThermometerSun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const WarehouseEPC = () => {
  const faqItems = [
    {
      question: 'Do warehouses need an EPC certificate?',
      answer: 'Yes, all warehouses in England require an Energy Performance Certificate when being sold, let or constructed. This applies to distribution centres, storage facilities, logistics hubs and any commercial storage buildings over 50m² with a roof and walls.'
    },
    {
      question: 'What EPC rating do warehouses typically achieve?',
      answer: 'Most warehouses achieve ratings between D and F. Older steel-frame warehouses with minimal insulation often rate E or F, while modern insulated distribution centres with LED lighting may achieve C or D. Cold storage facilities face additional challenges due to refrigeration energy demands.'
    },
    {
      question: 'How can I improve my warehouse EPC rating?',
      answer: 'The most effective improvements include upgrading to LED high-bay lighting (often improving ratings by 10-20 points), adding roof insulation, sealing roller shutter doors, installing heating controls and adding solar PV panels. Our assessors provide detailed recommendations with costs and payback periods.'
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Warehouse EPC Assessments | Distribution Centre EPCs | South West UK</title>
        <meta name="description" content="Professional EPC assessments for warehouses and distribution centres across Devon, Cornwall, Somerset and Dorset. SBEM certified assessors for logistics facilities." />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/warehouse-epc" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      
      <Navbar />
      <Breadcrumbs items={[{ label: 'Industrial EPC', path: '/industrial-epc' }, { label: 'Warehouse EPC', path: '/warehouse-epc' }]} />
      
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm mb-6">
              <Warehouse className="w-4 h-4" />
              Warehouse EPC Specialists
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Warehouse EPC Assessments</h1>
            <p className="text-xl text-blue-200 mb-4">Distribution Centres • Storage Facilities • Logistics Hubs</p>
            <p className="text-lg text-blue-300 mb-8 max-w-3xl mx-auto">
              Expert Energy Performance Certificates for warehouses and distribution centres across Devon, Cornwall, Somerset and Dorset. Our accredited assessors understand the unique energy characteristics of logistics facilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
                <Link to="/booking?service=commercial_warehouse">Get Warehouse EPC Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077"><Phone className="mr-2 w-5 h-5" />Call 01392 980077</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Energy Performance Certificates for Warehouses</h2>
            <p className="text-gray-700">
              Warehouses and distribution centres present distinct challenges for energy assessment. These buildings typically feature large open floor areas, high ceilings reaching 10-15 metres, minimal insulation in older structures, multiple roller shutter doors creating thermal bridges, and industrial heating systems designed for intermittent occupancy rather than continuous comfort.
            </p>
            <p className="text-gray-700">
              Our accredited non-domestic energy assessors have extensive experience with warehouse EPC surveys throughout the South West. We assess distribution centres, cold storage facilities, logistics hubs, pallet storage warehouses and multi-unit industrial estates, using SBEM methodology to produce accurate, compliant certificates.
            </p>
            <p className="text-gray-700">
              A warehouse EPC is legally required when selling or letting the property, when completing construction, and for MEES compliance on rental properties. Since April 2023, all rental warehouses must achieve at least an E rating or the landlord must register a valid exemption.
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">What Affects Warehouse EPC Ratings?</h2>
            <p className="text-gray-700">
              Warehouse energy performance is influenced by several key factors that our assessors examine during the survey:
            </p>
            <ul className="text-gray-700">
              <li><strong>High-Bay Lighting:</strong> Traditional sodium or metal halide high-bay lights consume significant energy. LED replacements can reduce lighting energy by 50-70% and substantially improve EPC ratings.</li>
              <li><strong>Roof Insulation:</strong> Heat loss through large uninsulated roofs dramatically affects ratings. Adding 100mm+ of insulation above the existing roof can improve ratings by 5-15 points.</li>
              <li><strong>Heating Systems:</strong> Gas-fired radiant heaters, warm air units and destratification fans all impact the assessment. Efficient, well-controlled systems perform better than older uncontrolled heaters.</li>
              <li><strong>Roller Shutter Doors:</strong> Large loading bay doors create significant thermal bridges and air leakage. Insulated doors with seals improve both comfort and EPC ratings.</li>
              <li><strong>Building Fabric:</strong> Wall insulation, cladding condition and glazing all contribute to the overall thermal performance.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Warehouse Types We Assess</h2>
            <p className="text-gray-700">Our warehouse EPC service covers all types of storage and distribution facilities:</p>
            <div className="grid md:grid-cols-2 gap-4 not-prose mt-6">
              {[
                { icon: Truck, title: 'Distribution Centres', desc: 'Large-scale logistics and fulfilment operations' },
                { icon: Package, title: 'Storage Warehouses', desc: 'Pallet storage, self-storage and archive facilities' },
                { icon: ThermometerSun, title: 'Cold Storage', desc: 'Refrigerated and temperature-controlled warehouses' },
                { icon: Warehouse, title: 'Trade Counters', desc: 'Hybrid warehouse/retail trade premises' }
              ].map((item) => (
                <Card key={item.title}>
                  <CardContent className="p-4 flex items-start gap-4">
                    <item.icon className="w-8 h-8 text-blue-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Warehouse EPC FAQs</h2>
            <div className="space-y-6">
              {faqItems.map((faq) => (
                <div key={faq.question} className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/industrial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <h3 className="font-semibold">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">All industrial building types</p>
              </Link>
              <Link to="/factory-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <h3 className="font-semibold">Factory EPCs</h3>
                <p className="text-sm text-gray-600">Manufacturing facilities</p>
              </Link>
              <Link to="/services/commercial_warehouse" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <h3 className="font-semibold">Commercial EPCs</h3>
                <p className="text-sm text-gray-600">All commercial buildings</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Get Your Warehouse EPC Quote</h2>
          <p className="text-xl text-green-100 mb-8">Fast, professional assessments across Devon, Cornwall, Somerset and Dorset.</p>
          <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
            <Link to="/booking?service=commercial_warehouse">Request Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default WarehouseEPC;